import React, { useMemo } from 'react';

import { useLocalSearchParams, usePathname } from 'expo-router';
import { Platform } from 'react-native';

import { useMenuScroll } from 'hooks/use-menu-scroll';
import { MenuHeaderNavigation } from 'pages/menu/components/menu-header-navigation';
import MenuTileGrid from 'pages/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'pages/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { MainMenuObject } from 'pages/menu/components/menu-tile-grid/menu-tile/types';
import { useFetchMainMenuOnMount } from 'pages/menu/hooks/use-fetch-main-menu';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'pages/menu/hooks/use-get-variant-content-options';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeContext } from 'state/service-mode';

import { MenuItemUnavailable } from '../../components/menu-item-unavailable';
import { reduceMenuSectionOptions } from '../../main-menu-page/utils';
import { useLogMenuContent } from '../use-log-menu-content';

import { MenuSectionTabs } from './menu-section-tabs';
import { useFilterSectionOptionsByDayPart } from './use-filter-section-options-by-day-part';

// OLD
export const MenuSection = () => {
  const pathname = usePathname();
  const { id } = useLocalSearchParams<{ id: string }>();
  useFetchMainMenuOnMount();

  useMenuScroll();

  const {
    data,
    getPricingAndAvailability,
    getSection,
    isMenuLoading,
    isOffer,
  } = useMainMenuContext();
  const { serviceMode } = useServiceModeContext();

  const filterSectionOptionsByDayPart = useFilterSectionOptionsByDayPart();

  const menuSection = useMemo(() => filterSectionOptionsByDayPart(getSection(id)), [
    filterSectionOptionsByDayPart,
    getSection,
    id,
  ]);

  const { inlineTileSections, standardSections, totalSectionsLength } = useMemo(
    () =>
      reduceMenuSectionOptions({
        // TODO: There is a type mismatch here, this need to be fixed in a future refactor
        // @ts-ignore
        menuSectionOptions: menuSection?.options,
        serviceMode,
        getPricingAndAvailability,
      }),
    [getPricingAndAvailability, menuSection?.options, serviceMode]
  );

  useLogMenuContent({
    loggedValues: {
      id: menuSection?._id || '',
      name: menuSection?.name?.locale || '',
      menuType: menuSection?._type || '',
    },
    pathname,
  });

  const getVariantContentOptions = useGetVariantContentOptions();

  if (isMenuLoading) {
    return (
      <>
        <MenuHeaderNavigation title="" />
        <MenuSkeletonLoader />
      </>
    );
  }

  // Review this
  if (!menuSection || totalSectionsLength <= 0) {
    return <MenuItemUnavailable isOffer={isOffer} />;
  }

  const { name } = getVariantContentOptions(menuSection as IMenuObjectWithContentOptions);
  const menuSectionName = name?.locale ?? '';

  return (
    <>
      <MenuHeaderNavigation title={menuSectionName} key={menuSectionName} />
      <MenuSectionTabs menuData={data?.Menu} menuDataLoading={isMenuLoading} />
      <RenderStandard id={id} sections={standardSections} />
      <RenderInlineTileGrid sections={inlineTileSections} />
    </>
  );
};

interface IMenuSectionContentTilesProps {
  sections: MainMenuObject[];
}

const RenderInlineTileGrid = React.memo<IMenuSectionContentTilesProps>(({ sections }) => {
  return (
    <>
      {sections.map(section => (
        <MenuTileGrid
          key={section._id}
          // TODO: There is a type mismatch here, this need to be fixed in a future refactor
          // @ts-ignore
          data={section.options || []}
          gridTitle={section.name?.locale}
          objectFitContain={true}
        />
      ))}
    </>
  );
});

const BOTTOM_SAFE_AREA_IOS = 30;

const RenderStandard = React.memo<IMenuSectionContentTilesProps & { id: string }>(
  ({ sections, id }) => {
    const paddingBottomSafeArea = Platform.select({
      default: 0,
      ios: BOTTOM_SAFE_AREA_IOS,
    });

    return sections.length > 0 ? (
      <MenuTileGrid
        key={id}
        data={sections}
        paddingBottom={paddingBottomSafeArea}
        objectFitContain
      />
    ) : null;
  }
);
